import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Check } from 'lucide-react';
import FormCheckbox from './FormCheckbox';
import FormTextArea from './FormTextArea';

const AttachmentsChecklist = ({ formData, onDataUpdate }) => {
  const attachmentsData = formData.attachments || [];
  const notes = formData.attachmentNotes || '';
  const uploadedFiles = formData.uploadedFiles || [];
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleCheckboxChange = (value) => {
    const updated = attachmentsData.includes(value)
      ? attachmentsData.filter(v => v !== value)
      : [...attachmentsData, value];
    onDataUpdate({ attachments: updated });
  };

  const handleNotesChange = (value) => {
    onDataUpdate({ attachmentNotes: value });
  };

  const processFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      addedAt: new Date().toISOString()
    }));
    onDataUpdate({ uploadedFiles: [...uploadedFiles, ...newFiles] });
  }, [uploadedFiles, onDataUpdate]);

  const handleFileSelect = (e) => {
    if (e.target.files?.length) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeFile = (id) => {
    onDataUpdate({ uploadedFiles: uploadedFiles.filter(f => f.id !== id) });
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) processFiles(e.dataTransfer.files);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const requiredDocs = [
    'Psychiatric evaluation', 'Diagnostic assessment', 'Progress notes', 'Medication list',
    'Insurance card (copy)', 'Referral script / order', 'Consent forms', 'Discharge summary',
    'School records / IEP (for minor)', 'Labs / medical records', 'Release of Information (ROI)',
    'Treatment history summary', 'Court orders (if applicable)', 'Other (please specify)'
  ];

  return (
    <div className="space-y-8">
      <div className="text-center border-b-2 border-gray-600 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Required Documents Checklist</h1>
        <p className="text-sm text-gray-600 mt-2">Please attach all applicable documentation</p>
      </div>

      <section className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
        <h3 className="font-bold text-blue-900 mb-2">Important Instructions:</h3>
        <ul className="text-sm text-blue-900 space-y-1 list-disc list-inside">
          <li>Check all documents that are attached to this referral</li>
          <li>Upload digital documents using the upload area below</li>
          <li>Accepted formats: PDF, JPG, PNG (up to 10MB each)</li>
          <li>Missing documents may delay processing</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-600 pb-2">Attached Documents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {requiredDocs.map((doc) => (
            <div key={doc} className="bg-white border border-gray-300 rounded-lg p-3 hover:bg-gray-50 transition-colors">
              <FormCheckbox label={doc} checked={attachmentsData.includes(doc)} onChange={() => handleCheckboxChange(doc)} />
            </div>
          ))}
        </div>
      </section>

      {/* Functional File Upload */}
      <section className="bg-gray-100 p-6 rounded-lg border-2 border-gray-300">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Digital File Upload</h2>
        <p className="text-sm text-gray-600 mb-4">Upload documents here. Accepted formats: PDF, JPG, PNG</p>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-400 bg-white hover:border-blue-400 hover:bg-blue-50/50'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="file-upload-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-upload-input"
          />
          <Upload className={`mx-auto h-10 w-10 mb-3 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className="text-sm text-gray-700">
            <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG up to 10MB each</p>
        </div>

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="uploaded-files-list">
            <h3 className="text-sm font-semibold text-gray-700">Uploaded Files ({uploadedFiles.length})</h3>
            {uploadedFiles.map((file) => (
              <div key={file.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-2">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[200px] md:max-w-[400px]">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(file.id); }} className="p-1 hover:bg-red-50 rounded transition-colors" data-testid={`remove-file-${file.id}`}>
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-gray-600 pb-2">Notes for Missing Documentation</h2>
        <FormTextArea label="Explain any missing documents or provide additional information" value={notes} onChange={handleNotesChange} rows={4} placeholder="Example: Psychiatric evaluation scheduled for next week, will submit upon completion" />
      </section>

      <section className="bg-green-50 p-6 rounded-lg border-2 border-green-300">
        <h3 className="font-bold text-green-900 mb-2">Checklist Summary</h3>
        <div className="text-sm text-green-900">
          <p className="mb-1"><span className="font-semibold">Documents Checked:</span> {attachmentsData.length} of {requiredDocs.length}</p>
          <p className="mb-1"><span className="font-semibold">Files Uploaded:</span> {uploadedFiles.length}</p>
          <p className="text-xs text-green-700 mt-2">Tip: Most referrals require at minimum: diagnostic assessment, consent forms, and insurance information</p>
        </div>
      </section>
    </div>
  );
};

export default AttachmentsChecklist;
